import {api} from '@bitstillery/common/app'
import m, {type Children, MithrilComponent} from '@bitstillery/mithril'
import {cached} from '@bitstillery/common/lib/api_cache'
import {unchecked} from '@bitstillery/common/lib/unchecked'

import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

function is_empty(data: unknown): boolean {
    if (data == null) {
        return true
    }
    return Array.isArray(data) && data.length === 0
}

/**
 * GET any `source`+`params` and cache the `result` body, keyed per user (safe default) and carried
 * across the SSR boundary. Empty results are not stored, so a component can keep retrying session-gated
 * content until it appears (see `AsyncData`'s retry-on-empty).
 */
const load_source = cached(
    'async-data',
    async (
        source: string,
        params?: Record<string, unknown>,
        method: 'get' | 'post' = 'get',
        body?: Record<string, unknown>,
    ): Promise<unknown> => {
        // v2 (fact2server) POST for endpoints that only accept a body (e.g. `portal/offer-items`); GET otherwise.
        const {result, status_code} =
            method === 'post' ? await api.post<unknown>(source, body ?? {}, true) : await api.get<unknown>(source, params)
        // A not-yet-generated relation price list answers offer-items with a 404 and an error body, not rows.
        // Throw so nothing is cached and `AsyncData` polls until the data is ready — like the shop collection.
        if (status_code === 404) {
            throw new Error(`async-data: source not ready (404): ${source}`)
        }
        return result
    },
    {
        ttl: 60_000,
        scope: 'user',
        key: (source: string, params?: Record<string, unknown>, method: 'get' | 'post' = 'get', body?: Record<string, unknown>) =>
            `${method}:${source}:${params ? JSON.stringify(params) : ''}:${body ? JSON.stringify(body) : ''}`,
        store_if: (value) => !is_empty(value),
    },
)

/** Called with `undefined` while a not-ready source is being polled (see `view`). */
type RenderFn<T> = (rows: T | undefined) => Children

export const asyncDataMeta = {
    tier: 'data',
    summary: 'Loads `source` and hands the rows to a child render function — keeps loading and content separate.',
    when_to_use:
        "Reuses the SSR snapshot (keyed by `source`) and hides when empty. `rows` is the `result` body of the matching `http_api` entry — match `source` to a listed path and read that path's `response` shape (destructuring the documented top-level key). Most sources are GET with `params`; for a POST endpoint (e.g. `portal/offer-items`) set `method='post'` and pass its request fields as `body`.",
    example: `
    <AsyncData source="portal/categories">
        {(rows) => (
            <Grid min="14rem">
                {rows.map((category) => (
                    <Card key={category.artkey} title={category.name} />
                ))}
            </Grid>
        )}
    </AsyncData>
    `,
} satisfies CmsComponentMeta

export interface AsyncDataAttrs<T = unknown> {
    /** Portal API path (no leading slash) — must be a path listed under `http_api`, e.g. `portal/categories`. */
    source: string
    /** Optional query params for the GET (the `http_api` entry's `query_params`). */
    params?: Record<string, unknown>
    /** HTTP method. Use `post` for endpoints that take a JSON body (e.g. `portal/offer-items`); default `get`. */
    method?: 'get' | 'post'
    /** JSON body sent when `method` is `post` (the endpoint's request fields); ignored for `get`. */
    body?: Record<string, unknown>
    /**
     * Render function receiving the loaded `result` body as `rows` (shape = the `source` path's `http_api` `response`).
     * Written as the child function: `<AsyncData source="…">{(rows) => <Grid>…</Grid>}</AsyncData>`. Also accepted as a `render` attr.
     */
    render?: RenderFn<T>
    children?: RenderFn<T>
}

/** Pull the render function from a `render` attr, an attr-routed child (shim), or raw children. */
function render_fn<T>(vnode: m.Vnode<AsyncDataAttrs<T>>): RenderFn<T> | null {
    const from_attr = vnode.attrs.render ?? vnode.attrs.children
    if (typeof from_attr === 'function') {
        return from_attr
    }
    const children: unknown = vnode.children
    const child: unknown = Array.isArray(children) ? children[0] : children
    // Mithril's `Children` has no function child; the CMS JSX runtime passes the render function through as one.
    return typeof child === 'function' ? unchecked<RenderFn<T>>(child) : null
}

/**
 * Loads `source` and hands the rows to the child render function — keeping data loading and content
 * separate. Reuses the SSR snapshot (keyed by `source`) on hydration, and while the result is still
 * empty retries after mount and on every navigation (covers session-gated content). Renders nothing
 * until data is present, and hides when the render output is empty.
 */
export class AsyncData<T = unknown> extends MithrilComponent<AsyncDataAttrs<T>> {
    private status: 'failed' | 'ok' | 'pending' = 'pending'
    /** The `source` body, whose shape `T` is the caller's reading of the `http_api` entry. */
    private data: unknown
    private on_locationchange: (() => void) | undefined
    private unmounted = false
    /** Retry timer for a source whose data isn't generated yet (the 404 not-ready state). */
    private poll_timer: ReturnType<typeof globalThis.setTimeout> | undefined

    override oninit(vnode: m.Vnode<AsyncDataAttrs<T>>) {
        // Warm read: cross-request cache on the server, or the SSR handoff on the client's first render.
        const snapshot = load_source.peek(vnode.attrs.source, vnode.attrs.params, vnode.attrs.method, vnode.attrs.body)
        if (snapshot !== undefined && !is_empty(snapshot)) {
            this.data = snapshot
            this.status = 'ok'
            return undefined
        }
        return this.fetch(vnode.attrs)
    }

    override oncreate(vnode: m.VnodeDOM<AsyncDataAttrs<T>>) {
        if (globalThis.__SSR_MODE__) {
            return
        }
        const retry_if_empty = () => {
            if (this.status === 'ok' && is_empty(this.data)) {
                void this.fetch(vnode.attrs)
            }
        }
        this.on_locationchange = () => globalThis.setTimeout(retry_if_empty, 0)
        globalThis.setTimeout(retry_if_empty, 0)
        window.addEventListener('locationchange', this.on_locationchange)
    }

    override onremove() {
        this.unmounted = true
        if (this.on_locationchange) {
            window.removeEventListener('locationchange', this.on_locationchange)
            this.on_locationchange = undefined
        }
        this.clear_poll()
    }

    private clear_poll() {
        if (this.poll_timer !== undefined) {
            globalThis.clearTimeout(this.poll_timer)
            this.poll_timer = undefined
        }
    }

    /** While the source isn't ready (404), re-fetch every couple of seconds until it succeeds — like the shop collection. */
    private schedule_poll(attrs: AsyncDataAttrs<T>) {
        if (globalThis.__SSR_MODE__ || this.unmounted || this.poll_timer !== undefined) {
            return
        }
        this.poll_timer = globalThis.setTimeout(() => {
            this.poll_timer = undefined
            void this.fetch(attrs)
        }, 2000)
    }

    view(vnode: m.Vnode<AsyncDataAttrs<T>>) {
        // 'ok' hands the loaded rows to the child. 'failed' means the source isn't ready yet (a 404 we keep
        // polling): on the client, still render the child with `undefined` so it can show its own loading
        // state — a child that ignores it simply renders empty and is hidden below. 'pending' (first fetch in
        // flight) and any not-ready state during SSR render nothing (nothing to hand off; the client re-fetches).
        const not_ready = this.status === 'failed' && !globalThis.__SSR_MODE__
        if (this.status !== 'ok' && !not_ready) {
            return null
        }
        const render = render_fn(vnode)
        if (render == null) {
            return null
        }
        const content = render(this.status === 'ok' ? unchecked<T>(this.data) : undefined)
        if (content == null || content === false || (Array.isArray(content) && content.length === 0)) {
            return null
        }
        return content
    }

    private async fetch(attrs: AsyncDataAttrs<T>): Promise<void> {
        try {
            const result = await load_source(attrs.source, attrs.params, attrs.method, attrs.body)
            if (this.unmounted) {
                return
            }
            this.data = result
            this.status = 'ok'
            this.clear_poll()
        } catch {
            if (this.unmounted) {
                return
            }
            this.status = 'failed'
            // Source not ready (e.g. the relation's price list is still generating) — keep polling until it appears.
            this.schedule_poll(attrs)
        }
        if (!globalThis.__SSR_MODE__) {
            m.redraw()
        }
    }
}
